import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { X, ExternalLink, FileText, User, Tag, Bell, MessageSquare, CheckSquare, Square, Trash2, Phone } from 'lucide-react';
import toast from 'react-hot-toast';
import api, { leadsAPI } from '../services/api';
import ConfirmationModal from './ConfirmationModal';
import VoipRecordingPlayer from './VoipRecordingPlayer';
import MetaLeadFormCard, { looksLikeFormSubmission, FORM_NOTE_SOURCES } from './MetaLeadFormCard';
import { useAuth } from '../contexts/AuthContext';
import useLeadClaim from '../hooks/useLeadClaim';
import BrowserCallButton from './BrowserCallButton';
import OpenDentalSyncBadge from './OpenDentalSyncBadge';
import AdAttributionBadge from './AdAttributionBadge';
import { useOpenDentalBooking } from '../contexts/OpenDentalBookingContext';
import ReengagedNotice from './ReengagedNotice';

/**
 * Slide-out drawer for the Pipeline board. Mirrors the "Lead
 * management" panel Meta's Leads Center surfaces — Owner, Stage,
 * Reminder, Notes, Form answers — so an operator can triage a card
 * without leaving the board. The full Lead Detail page is one click
 * away via the "Open full profile" link.
 *
 * Loads its own data when opened so the parent (PipelineBoard) can
 * stay narrowly scoped to columns / drag-drop. Closes on Escape and
 * on backdrop click.
 */
// Caller provides orgUsers + stages — both already loaded in
// PipelineBoard, no point re-fetching them here.
const PipelineLeadDrawer = ({ leadId, orgUsers = [], stages = [], onClose }) => {
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();
  const { interceptWonTransition } = useOpenDentalBooking();

  const { data: leadResp, isLoading } = useQuery(
    ['lead', leadId],
    () => leadsAPI.getLead(leadId),
    { enabled: !!leadId, refetchOnWindowFocus: false }
  );
  const lead = leadResp?.data || leadResp;
  // First-come-first-served — clicking the phone link claims the lead.
  const claim = useLeadClaim({ lead, currentUser });

  // Local edit state. Owner + Stage are surfaced as a draft you commit
  // with the Save button so the operator can change both in one round
  // trip instead of triggering a save per dropdown change. Drafts seed
  // from the lead doc when it loads / the drawer reopens for a different
  // card.
  const [draftAssignedTo, setDraftAssignedTo] = useState('');
  const [draftStageId, setDraftStageId] = useState('');
  useEffect(() => {
    if (!lead) return;
    setDraftAssignedTo(lead.assignedTo || '');
    setDraftStageId(lead.stageId || '');
  }, [lead?._id, lead?.assignedTo, lead?.stageId]); // eslint-disable-line react-hooks/exhaustive-deps

  const isDirty =
    !!lead && (
      String(draftAssignedTo || '') !== String(lead.assignedTo || '') ||
      String(draftStageId || '') !== String(lead.stageId || '')
    );

  // Esc to close.
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const updateLead = useMutation(
    (patch) => leadsAPI.updateLead(leadId, patch),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['lead', leadId]);
        // Pipeline board key is `['pipeline-board', pipelineId, opts]`.
        // Use a predicate so we hit every board view (with/without
        // closed stages) instead of guessing the exact key shape.
        queryClient.invalidateQueries({
          predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === 'pipeline-board',
        });
        toast.success('Saved');
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Update failed'),
    }
  );

  const handleSave = async () => {
    if (!isDirty) return;
    // OpenDental: moving the lead into a Won stage books an appointment
    // first. Resolves 'proceed' immediately unless the add-on is configured
    // and this is a fresh Won transition.
    const stageChanged = String(draftStageId || '') !== String(lead?.stageId || '');
    if (stageChanged && draftStageId) {
      const targetStage = stages.find((s) => String(s._id) === String(draftStageId));
      if (targetStage?.closedType === 'won') {
        const decision = await interceptWonTransition({ lead, targetStage });
        if (decision === 'cancelled') return;
      }
    }
    updateLead.mutate({
      assignedTo: draftAssignedTo || null,
      stageId: draftStageId || null,
    });
  };

  // Follow-up tasks for this lead — feed the Reminder section. Reminders
  // were a single date field on the lead before; now they live on the
  // Task entity so the rest of the CRM (Dashboard widgets, due-date
  // notifications, snooze, complete) gets them for free.
  const { data: tasksResp } = useQuery(
    ['lead-tasks', leadId],
    () => api.get('/tasks', { params: { leadId } }).then((r) => r.data),
    { enabled: !!leadId, refetchOnWindowFocus: false }
  );
  const leadTasks = (Array.isArray(tasksResp) ? tasksResp : (tasksResp?.tasks || []))
    .filter((t) => !t.completed)
    .sort((a, b) => new Date(a.dueDate || 0) - new Date(b.dueDate || 0));

  const createTask = useMutation(
    (body) => api.post('/tasks', body).then((r) => r.data),
    {
      onSuccess: () => {
        setReminderAt('');
        queryClient.invalidateQueries(['lead-tasks', leadId]);
        toast.success('Follow-up scheduled');
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Could not schedule follow-up'),
    }
  );

  const updateTask = useMutation(
    ({ id, patch }) => api.patch(`/tasks/${id}`, patch).then((r) => r.data),
    { onSuccess: () => queryClient.invalidateQueries(['lead-tasks', leadId]) }
  );

  const deleteTask = useMutation(
    (id) => api.delete(`/tasks/${id}`),
    { onSuccess: () => queryClient.invalidateQueries(['lead-tasks', leadId]) }
  );

  const addNote = useMutation(
    (content) => api.post(`/leads/${leadId}/notes`, { content }).then((r) => r.data),
    {
      onSuccess: () => {
        setNoteDraft('');
        queryClient.invalidateQueries(['lead', leadId]);
        toast.success('Note added');
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Could not add note'),
    }
  );

  const deleteNote = useMutation(
    (noteId) => api.delete(`/leads/${leadId}/notes/${noteId}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['lead', leadId]);
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Could not delete note'),
    }
  );

  const [noteDraft, setNoteDraft] = useState('');
  const [reminderAt, setReminderAt] = useState('');
  const [noteToDelete, setNoteToDelete] = useState(null);

  // Internal notes only — same filter as Lead Detail's Notes panel.
  // Show the latest few inline; link out to the full timeline for the rest.
  const NOTES_SHOWN = 5;
  const internalNotes = (lead?.notes || [])
    .filter((n) => ['web', 'appointment'].includes(n?.source || 'web'))
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  const shownNotes = internalNotes.slice(0, NOTES_SHOWN);

  // Call recordings — phone notes the CDR poller stamped with an audio file.
  // Newest first; we show the latest few inline and link out for the rest.
  const RECORDINGS_SHOWN = 5;
  const recordings = (lead?.notes || [])
    .filter((n) => n?.source === 'phone' && cfOf(n).has_recording)
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  const shownRecordings = recordings.slice(0, RECORDINGS_SHOWN);

  // Most recent meta_ad note carries the form recap content. We pull
  // its customFields (which already has phone/email/full_name etc.)
  // plus the leadgen_id / form_id / submitted_at metadata.
  // Click-to-Messenger Lead Ads sometimes only produce a messenger /
  // instagram note (no separate leadgen webhook), so fall back to the
  // first DM whose body looks like a form submission.
  const allNotes = lead?.notes || [];
  const byNewest = (a, b) => new Date(b.createdAt) - new Date(a.createdAt);
  // Any form-source note (Meta/Google/TikTok ad OR a website web-form
  // submission) carries the inbound form recap. Newest wins. Fall back to a
  // Click-to-Messenger DM whose body looks like a form submission.
  const formNote =
    [...allNotes].filter((n) => FORM_NOTE_SOURCES.has(n?.source)).sort(byNewest)[0]
    || [...allNotes]
         .filter((n) => (n?.source === 'messenger' || n?.source === 'instagram')
                        && looksLikeFormSubmission(n?.content))
         .sort(byNewest)[0];

  // Parse "key: value" lines out of the meta_ad note content for the
  // Form answers section. The importer stores them in a stable shape
  // ("phone: ...\nemail: ...") so a simple split works.
  const parsedFormFields = (() => {
    if (!formNote?.content) return [];
    const lines = String(formNote.content).split('\n');
    const result = [];
    for (const line of lines) {
      const m = line.match(/^([^:]+):\s+(.*)$/);
      if (!m) continue;
      const key = m[1].trim();
      const val = m[2].trim();
      // Skip footer line "Form: ... · leadgen_id: ..." — handled separately.
      if (/^Form\s*$/i.test(key)) continue;
      if (key.startsWith('Meta ad_id') || key === 'leadgen_id' || key === 'ad_id') continue;
      // Attribution recap line ("Campaign: X · Ad set: Y · Ad: Z") — the
      // badge under the lead name already shows it.
      if (key === 'Campaign' || key === 'Ad set' || key === 'Ad') continue;
      result.push({ key, val });
    }
    return result;
  })();

  const cf = lead?.customFields || {};
  const cfGet = (k) => (typeof cf.get === 'function' ? cf.get(k) : cf[k]);
  // customFields wins (set explicitly when the leadgen webhook captured
  // the form), but for Click-to-Messenger leads where the form arrived
  // via the DM the leadgen id only lives on the lead's tags. Fall back
  // to that so the footer hash still appears.
  const leadgenIdFromTag = (lead?.tags || [])
    .find((t) => typeof t === 'string' && /^meta:leadgen:/.test(t))
    ?.split(':')[2] || null;
  const leadgenId = cfGet('meta_leadgen_id') || leadgenIdFromTag;
  const formName = cfGet('meta_form_name');
  const submittedAt = formNote?.createdAt;

  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} />
      <aside className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-white shadow-xl z-50 overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-5 py-3 flex items-center justify-between z-10">
          <h2 className="text-lg font-semibold text-gray-900">Lead management</h2>
          <button onClick={onClose} className="p-1 text-gray-500 hover:text-gray-900 rounded">
            <X size={18} />
          </button>
        </div>

        {isLoading || !lead ? (
          <div className="p-6 text-sm text-gray-500">Loading…</div>
        ) : (
          <div className="p-5 space-y-6">
            {/* Lead identity */}
            <div>
              <Link
                to={`/leads/${leadId}`}
                className="inline-flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700"
              >
                <ExternalLink size={12} /> Open full profile
              </Link>
              <h3 className="text-xl font-bold text-gray-900 mt-2">
                {`${lead.firstName || ''} ${lead.lastName || ''}`.trim() || '(no name)'}
              </h3>
              <AdAttributionBadge lead={lead} compact className="mt-1.5" />
              <div className="text-xs text-gray-500 mt-1 space-x-2">
                {lead.email && <span>{lead.email}</span>}
                {lead.phone && (
                  <span>
                    {' · '}
                    <a
                      href={`tel:${lead.phone}`}
                      className={`text-primary-600 hover:text-primary-700 ${claim.locked ? 'opacity-50 cursor-not-allowed' : ''}`}
                      onClick={(e) => {
                        // Admin clicking an unassigned lead still claims —
                        // they're the one taking the call. Skip only when
                        // the lead is already theirs.
                        if (claim.ownerSelf) return;
                        e.preventDefault();
                        claim.requestClaim(() => { window.location.href = `tel:${lead.phone}`; });
                      }}
                    >
                      {lead.phone}
                    </a>
                    {' '}
                    <BrowserCallButton
                      lead={lead}
                      phone={lead.phone}
                      size={13}
                      disabled={claim.locked}
                      guard={(go) => (claim.ownerSelf ? go() : claim.requestClaim(go))}
                    />
                  </span>
                )}
              </div>
              {/* First-come-first-served ownership badge */}
              {claim.unassigned && (
                <div className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-50 text-amber-800 border border-amber-100">
                  🔓 Open lead
                </div>
              )}
              {claim.locked && (
                <div className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-50 text-red-800 border border-red-100">
                  🔒 Owned by {claim.ownerName}
                </div>
              )}
            </div>

            {/* Owner — admins can reassign freely; non-admins see the
                current owner read-only and can only claim if the lead
                is unassigned. Reassigning someone else's lead is an
                admin-only action everywhere (LeadDetail, LeadsList
                already enforce this); the drawer was the last gap. */}
            <Section icon={<User size={14} />} title="Assigned to">
              {claim.isAdmin ? (
                <select
                  value={draftAssignedTo || ''}
                  onChange={(e) => setDraftAssignedTo(e.target.value)}
                  disabled={updateLead.isLoading}
                  className="w-full text-sm rounded-md border border-gray-300 bg-white px-2 py-1.5 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">— Unassigned —</option>
                  {orgUsers.map((u) => (
                    <option key={u._id} value={u._id}>{u.name || u.email}</option>
                  ))}
                </select>
              ) : (
                <div className="w-full text-sm rounded-md border border-gray-200 bg-gray-50 px-2 py-1.5 text-gray-700">
                  {claim.ownerName || '— Unassigned —'}
                </div>
              )}
              {claim.unassigned && (
                <button
                  type="button"
                  onClick={() => claim.requestClaim()}
                  className="mt-2 w-full inline-flex items-center justify-center gap-1.5 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-md text-xs font-medium"
                  title="Reserve this lead before someone else grabs it"
                >
                  🔓 Take this lead
                </button>
              )}
            </Section>

            {lead?.reengagedAt && <ReengagedNotice lead={lead} className="mb-3" />}

            {/* Stage */}
            <Section icon={<Tag size={14} />} title="Stage">
              <select
                value={draftStageId || ''}
                onChange={(e) => setDraftStageId(e.target.value)}
                disabled={updateLead.isLoading}
                className="w-full text-sm rounded-md border border-gray-300 bg-white px-2 py-1.5 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">— No stage —</option>
                {stages.map((s) => (
                  <option key={s._id} value={s._id}>{s.name}</option>
                ))}
              </select>
              {lead?.opendental?.syncStatus && lead.opendental.syncStatus !== 'none' && (
                <div className="mt-2">
                  <OpenDentalSyncBadge status={lead.opendental.syncStatus} />
                </div>
              )}
            </Section>

            {/* Save bar — visible only when the operator has unsaved
                edits to Owner / Stage. Sticks to the bottom of the
                drawer's scroll area so it's reachable without scrolling
                back up from the Notes / Form-answers sections. */}
            {isDirty && (
              <div className="sticky bottom-0 -mx-5 px-5 py-3 bg-white border-t border-gray-200 flex items-center justify-end gap-2 z-10">
                <button
                  onClick={() => {
                    setDraftAssignedTo(lead.assignedTo || '');
                    setDraftStageId(lead.stageId || '');
                  }}
                  disabled={updateLead.isLoading}
                  className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={updateLead.isLoading}
                  className="px-4 py-1.5 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white rounded-md text-sm font-medium"
                >
                  {updateLead.isLoading ? 'Saving…' : 'Save'}
                </button>
              </div>
            )}

            {/* Reminder = a Task with type=todo. Schedules a follow-up
                that the rest of the CRM (Dashboard, Tasks page,
                notifications) already handles end-to-end — completion,
                snooze, owner reassignment, due-date highlight. */}
            <Section icon={<Bell size={14} />} title="Reminder">
              <div className="flex gap-2">
                <input
                  type="datetime-local"
                  value={reminderAt}
                  onChange={(e) => setReminderAt(e.target.value)}
                  className="flex-1 text-sm rounded-md border border-gray-300 bg-white px-2 py-1.5 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
                <button
                  onClick={() => {
                    if (!reminderAt) return;
                    const leadName = `${lead.firstName || ''} ${lead.lastName || ''}`.trim() || 'lead';
                    createTask.mutate({
                      leadId,
                      title: `Follow up: ${leadName}`,
                      type: 'todo',
                      dueDate: new Date(reminderAt).toISOString(),
                      assignedTo: lead.assignedTo || undefined,
                    });
                  }}
                  disabled={!reminderAt || createTask.isLoading}
                  className="px-3 py-1.5 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white rounded-md text-xs font-medium whitespace-nowrap"
                >
                  Schedule
                </button>
              </div>
              {leadTasks.length > 0 && (
                <ul className="mt-3 space-y-1.5">
                  {leadTasks.map((t) => {
                    const due = t.dueDate ? new Date(t.dueDate) : null;
                    const overdue = due && due < new Date();
                    return (
                      <li key={t._id} className="flex items-center gap-2 text-xs bg-gray-50 border border-gray-200 rounded px-2 py-1.5">
                        <button
                          onClick={() => updateTask.mutate({ id: t._id, patch: { completed: true } })}
                          className="flex-shrink-0 text-gray-400 hover:text-green-600"
                          title="Complete"
                        >
                          <Square size={14} />
                        </button>
                        <div className="flex-1 min-w-0">
                          <div className="text-gray-900 truncate">{t.title}</div>
                          {due && (
                            <div className={`text-[11px] ${overdue ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                              {overdue ? 'Overdue · ' : ''}{due.toLocaleString()}
                            </div>
                          )}
                        </div>
                        <button
                          onClick={() => deleteTask.mutate(t._id)}
                          className="flex-shrink-0 text-gray-400 hover:text-red-600"
                          title="Delete"
                        >
                          <Trash2 size={12} />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </Section>

            {/* Notes */}
            <Section icon={<MessageSquare size={14} />} title="Notes">
              <div className="space-y-2">
                <textarea
                  value={noteDraft}
                  onChange={(e) => setNoteDraft(e.target.value.slice(0, 1000))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                      e.preventDefault();
                      if (noteDraft.trim()) {
                        claim.requestClaim(() => addNote.mutate(noteDraft));
                      }
                    }
                  }}
                  placeholder="Write a note... (⌘+Enter to save)"
                  className="w-full text-sm rounded-md border border-gray-300 bg-white px-2 py-1.5 resize-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  rows={2}
                />
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>{noteDraft.length}/1000</span>
                  <button
                    onClick={() => claim.requestClaim(() => addNote.mutate(noteDraft))}
                    disabled={!noteDraft.trim() || addNote.isLoading}
                    className="px-3 py-1 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white rounded text-xs font-medium"
                  >
                    Add
                  </button>
                </div>
                <ul className="space-y-2 mt-2">
                  {shownNotes.map((n) => {
                    // createdBy comes back as raw ObjectId from
                    // getNotesByLead (no populate); resolve via the
                    // orgUsers map the parent already loaded so we
                    // don't pay for a populate in the API.
                    const authorId = n.createdBy?._id ? String(n.createdBy._id) : (n.createdBy ? String(n.createdBy) : null);
                    const authorObj = n.createdBy?.name || n.createdBy?.email
                      ? n.createdBy
                      : (authorId ? orgUsers.find((u) => String(u._id) === authorId) : null);
                    const authorLabel = authorObj?.name || authorObj?.email || (authorId ? 'Unknown user' : 'System');
                    return (
                      <li key={n._id} className="text-xs bg-gray-50 border border-gray-200 rounded p-2">
                        <div className="text-gray-700 whitespace-pre-wrap break-words">{n.content}</div>
                        <div className="mt-1 text-gray-400 flex items-center gap-2">
                          <span className="font-medium text-gray-600">{authorLabel}</span>
                          <span>·</span>
                          <span>{fullDateTime(n.createdAt)}</span>
                          <button
                            onClick={() => setNoteToDelete(n._id)}
                            className="ml-auto text-red-500 hover:text-red-700"
                          >Delete</button>
                        </div>
                      </li>
                    );
                  })}
                  {internalNotes.length === 0 && (
                    <li className="text-xs text-gray-400 italic">No notes yet.</li>
                  )}
                </ul>
                {internalNotes.length > shownNotes.length && (
                  <Link
                    to={`/leads/${leadId}`}
                    className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700"
                  >
                    See all {internalNotes.length} notes <ExternalLink size={12} />
                  </Link>
                )}
              </div>
            </Section>

            {/* Call recordings — only shown when the lead has at least one
                recorded phone call. Latest few play inline; the rest live
                on the full Lead Detail timeline. */}
            {recordings.length > 0 && (
              <Section icon={<Phone size={14} />} title={`Call recordings (${recordings.length})`}>
                <ul className="space-y-3">
                  {shownRecordings.map((n) => {
                    const cf = cfOf(n);
                    const inbound = n.direction === 'inbound';
                    // Resolve the rep the same way the Notes list does:
                    // createdBy may be a populated object or a bare id.
                    const authorId = n.createdBy?._id ? String(n.createdBy._id) : (n.createdBy ? String(n.createdBy) : null);
                    const authorObj = n.createdBy?.name || n.createdBy?.email
                      ? n.createdBy
                      : (authorId ? orgUsers.find((u) => String(u._id) === authorId) : null);
                    const authorLabel = authorObj?.name || authorObj?.email || null;
                    return (
                      <li key={n._id} className="text-xs bg-green-50 border border-green-200 rounded p-2">
                        <div className="flex items-center flex-wrap gap-x-2 gap-y-0.5 text-gray-500 mb-1.5">
                          <span className={`font-semibold ${inbound ? 'text-blue-700' : 'text-gray-700'}`}>
                            {inbound ? '← Inbound' : 'Outbound →'}
                          </span>
                          <span>·</span>
                          <span>{fullDateTime(n.createdAt)}</span>
                          {cf.duration_seconds != null && (
                            <>
                              <span>·</span>
                              <span>{formatCallDuration(cf.duration_seconds)}</span>
                            </>
                          )}
                          {authorLabel && (
                            <>
                              <span>·</span>
                              <span className="font-medium text-gray-600">{authorLabel}</span>
                            </>
                          )}
                        </div>
                        <VoipRecordingPlayer kind="note" id={n._id} />
                      </li>
                    );
                  })}
                </ul>
                {recordings.length > shownRecordings.length && (
                  <Link
                    to={`/leads/${leadId}`}
                    className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700"
                  >
                    See all {recordings.length} recordings <ExternalLink size={12} />
                  </Link>
                )}
              </Section>
            )}

            {/* Form answers — surfaces any inbound form submission: Meta /
                Google / TikTok ad recaps AND website web-form notes. When
                the content is "key: value" shaped it renders as a structured
                card; a free-text web-form note (e.g. "Booked a consult…")
                falls back to its raw text so it's never hidden. */}
            {formNote && (() => {
              const isStructured = parsedFormFields.length > 0 || !!leadgenId
                || looksLikeFormSubmission(formNote.content);
              const title = formNote.source === 'webform' ? 'Web form' : 'Form answers';
              return (
                <Section icon={<FileText size={14} />} title={title}>
                  {isStructured ? (
                    <MetaLeadFormCard
                      content={formNote.content}
                      formName={formName}
                      leadgenId={leadgenId}
                      submittedAt={submittedAt}
                      onPhoneClick={(phone, e) => {
                        if (claim.ownerSelf) return;
                        e.preventDefault();
                        claim.requestClaim(() => { window.location.href = `tel:${phone}`; });
                      }}
                      onEmailClick={(email, e) => {
                        if (claim.ownerSelf) return;
                        e.preventDefault();
                        claim.requestClaim(() => { window.location.href = `mailto:${email}`; });
                      }}
                    />
                  ) : (
                    <div className="text-xs bg-blue-50 border border-blue-200 rounded p-3">
                      <p className="text-gray-700 whitespace-pre-wrap break-words">{formNote.content}</p>
                      {submittedAt && (
                        <p className="text-[10px] text-gray-400 mt-2">{fullDateTime(submittedAt)}</p>
                      )}
                    </div>
                  )}
                </Section>
              );
            })()}
          </div>
        )}
      </aside>

      <ConfirmationModal {...claim.confirmProps} />
      <ConfirmationModal
        isOpen={!!noteToDelete}
        onClose={() => setNoteToDelete(null)}
        onConfirm={() => {
          deleteNote.mutate(noteToDelete, {
            onSettled: () => setNoteToDelete(null),
          });
        }}
        title="Delete Note"
        message="Are you sure you want to delete this note?"
        confirmText="Delete"
        isDanger
        isLoading={deleteNote.isLoading}
      />
    </>
  );
};

const Section = ({ icon, title, children }) => (
  <div>
    <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">
      {icon}
      {title}
    </div>
    {children}
  </div>
);

// Full date + time for note timestamps (the drawer shows the exact moment,
// not a relative "2h ago").
const fullDateTime = (d) => {
  if (!d) return '';
  return new Date(d).toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

// note.customFields may arrive as a plain object (JSON API) or a Map;
// normalise to a plain object for safe reads.
const cfOf = (note) => {
  const cf = note?.customFields;
  if (!cf) return {};
  return cf instanceof Map ? Object.fromEntries(cf) : cf;
};

// "17s" / "1m 5s" from a seconds count.
const formatCallDuration = (seconds) => {
  const s = Number(seconds || 0);
  if (s <= 0) return '0s';
  const m = Math.floor(s / 60);
  const r = s % 60;
  return m > 0 ? `${m}m${r ? ` ${r}s` : ''}` : `${r}s`;
};

export default PipelineLeadDrawer;
